import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { Observable, tap } from 'rxjs';
import { JwtService } from './jwt.service';
import {
  Account,
  UserProfile,
  UserProfileDetails,
} from '../utils/account';
import { ApiService } from 'src/app/core/api.service';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  IChangePassResponse,
  ICreateUser,
  ICreateUserResponce,
  IloginResponse,
  Login,
  RefreshToken,
  ResetPassword,
  Users,
} from '../utils/auth';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { jwtDecode } from 'jwt-decode';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { AlertService } from 'src/app/shared/services/alert.service';

const basePath: string = '/auth';
const basePathAuth: string = '/Auth';
const testLogin: string = '/loginTest';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  destroyRef = inject(DestroyRef);
  private readonly apiService = inject(ApiService);
  private readonly jwtService = inject(JwtService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly sessionStorageService = inject(SessionStorageService);
  readonly permissionsService = inject(NgxPermissionsService);

  private account = signal<Account>(new Account());
  public accountState = computed(this.account);
  alert = inject(AlertService);

  userProfileInit = {
    userProfile: new UserProfileDetails(),
    loaded: false,
  };

  private userProfileDetails = signal<UserProfile>(this.userProfileInit);

  userProfileState = computed(() => this.userProfileDetails().userProfile);
  userProfileLoaded = computed(() => this.userProfileDetails().loaded);

  login(params: Login) {
    return this.apiService.post(`${basePath}/login`, params).pipe(
      tap((res: IloginResponse) => {
        if (res.status !== 0) return;
        else {
          this.jwtService.saveToken('AccessToken', res.token);
          let decodedInfo = this.getDecodedAccessToken(res.token);
          this.setAccount(decodedInfo, res.token);
        }
      })
    );
  }

  private getTokenInfo(tokenData: any) {
    this.account.update((state) => ({
      ...tokenData.decodedTokenInfo,
      token: this.jwtService.getToken('AccessToken'),
    }));
  }

  private setPermissions(roles: string[]) {
    this.permissionsService.loadPermissions(roles);
  }

  private setAccount(account: Account, token: string) {
    // this.jwtService.saveToken('AccessToken',account.token);

    this.account.update((state) => ({
      ...account,
      token: token,
    }));
    this.setPermissions(account.Role);
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      return null;
    }
  }

  changePassword(params: ResetPassword): Observable<IChangePassResponse> {
    return this.apiService.post(`${basePath}/ChangePassword`, params);
  }

  createUser(params: ICreateUser): Observable<ICreateUserResponce> {
    return this.apiService.post(`${basePathAuth}/CreateUser`, params);
  }

  populate(tokenData: any) {
    var jwt = this.jwtService.getToken('AccessToken') ?? '';
    if (jwt) {
      this.getTokenInfo(tokenData);
    } else {
      this.logOut();
    }
  }

  logOut() {
    this.jwtService.destroyToken('AccessToken');
    this.localStorageService.destroyKey('smsLang');
    this.sessionStorageService.destroyKey('registrationFlowData');
    this.account.update((x) => (x = {} as Account));
    this.userProfileDetails.update((x) => (x = {} as UserProfile));
  }

  getUserList(params: any): Observable<Users[]> {
    return this.apiService.post(`${basePathAuth}/GetUserList`, params);
  }

  activateUser(id: number) {
    return this.apiService.post(`${basePathAuth}/ActivateUser`, { id });
  }

  deActivateUser(id: number) {
    return this.apiService.post(`${basePathAuth}/DeActivateUser`, { id });
  }
  unlockUser(id: number) {
    return this.apiService.post(`${basePathAuth}/UnlockUser`, { id });
  }
  resetPassword(id: number) {
    return this.apiService.post(`${basePathAuth}/ResetPassword`, { id });
  }

  //switched off

  getRefreshToken(params: RefreshToken) {
    return this.apiService.post(`${basePath}/RefreshToken`, params);
  }

  refreshToken(): Observable<any> {
    var refreshToken = this.jwtService.getToken('Rtoken');
    if (refreshToken == undefined) {
      throw 'Refresh token not found';
    }
    return this.getRefreshToken({ refreshToken: refreshToken })
  }
}
